<script>
import { mapActions, mapGetters } from "vuex";
import { ODataHeader, ODataProvider, ODataString } from "pbas-vue-components";
import OptOutsHeaders from "../../headers/OptOutsHeaders";
import EditOptOutForm from "./EditOptOutForm.vue";

export default {
  name: "OptOutsMemberList",
  components: { EditOptOutForm },
  props: {
    certificate: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapGetters("common", { selectedPlan: "plan" }),
    coverageGroupItems() {
      return [{ name: "All", id: "" }, ...this.coverageGroups];
    },
    provider() {
      const planId = this.selectedPlan.Id;
      let args = [`plan=${planId}`, `certificate='${this.certificate}'`];

      if (this.statusFilter) {
        args.push(`status='${this.statusFilter}'`);
      }

      if (this.coverageFilter) {
        args.push(`coverage=${this.coverageFilter}`);
      }

      const url = `optouts/odata/OptOutRequests/ByMemberList(${args.join(
        ","
      )})`;

      return new ODataProvider(this.$axiosHttp, url, this.headers);
    },
  },
  data() {
    return {
      selected: {},
      statusFilter: "",
      coverageFilter: "",
      statusFilters: [
        { text: "All", value: "" },
        { text: "Tentatively Approved", value: "TentativelyApproved" },
        { text: "Updated", value: "Updated" },
        { text: "Pending", value: "Pending" },
        { text: "Approved", value: "Approved" },
        { text: "Rejected", value: "Rejected" },
        { text: "Canceled", value: "Canceled" },
      ],
      configuration: {},
      optOuts: undefined,
      coverageGroups: [],
      headers: null,
      allHeaders: OptOutsHeaders,
      optOut: null,
      dialogs: {
        edit: false,
        view: false,
      },
      rowActions: [
        {
          name: "edit",
          icon: "edit",
          title: "globals.edit",
          handler: (item) => {
            this.selected = item;

            this.$nextTick(() => {
              this.dialogs.edit = true;
            });
          },
        },
      ],
    };
  },
  methods: {
    ...mapActions("optouts", [
      "loadOptOuts",
      "updateOptOut",
      "loadCoverageGroups",
      "loadConfiguration",
      "adminUpdate",
    ]),
    async load() {
      if (this.selectedPlan && this.selectedPlan.Id) {
        this.headers = [...this.allHeaders];
        const coverageGroups = await this.loadCoverageGroups(
          this.selectedPlan.Id
        );
        this.coverageGroups = coverageGroups;
        this.configuration = await this.loadConfiguration(this.selectedPlan.Id);

        coverageGroups.forEach((c) => {
          this.headers.push(
            new ODataHeader({
              title: "Status " + c.name,
              sortable: false,
              searchable: false,
              type: new ODataString("OptOuts/Statuses/Status,EffectiveDate", {
                getHtml: (items) => {
                  const optOut = items.OptOuts.find(
                    (x) => x.CoverageGroup.Name === c.name
                  );
                  if (optOut !== undefined && optOut.Statuses.length > 0) {
                    const latest = [...optOut.Statuses].sort(
                      (a, b) =>
                        new Date(b.EffectiveDate) - new Date(a.EffectiveDate)
                    )[0];
                    switch (latest.Status) {
                      case "Approved":
                        return (
                          `<div class="success font-weight-bold pa-2 white--text">` +
                          latest.Status +
                          `</div>`
                        );
                      case "Updated":
                        return (
                          `<div class="info font-weight-bold pa-2 white--text">` +
                          latest.Status +
                          `</div>`
                        );
                      case "Pending":
                        return (
                          `<div class="warning font-weight-bold pa-2 white--text">` +
                          latest.Status +
                          `</div>`
                        );
                      case "TentativelyApproved":
                        return (
                          `<div class="warning font-weight-bold pa-2 white--text">` +
                          latest.Status +
                          `</div>`
                        );
                      case "Cancelled":
                        return (
                          `<div class="error font-weight-bold pa-2 white--text">` +
                          latest.Status +
                          `</div>`
                        );
                      case "Rejected":
                        return (
                          `<div class="error font-weight-bold pa-2 white--text">` +
                          latest.Status +
                          `</div>`
                        );
                      default:
                        return (
                          `<div class="font-weight-bold pa-2">` +
                          latest.Status +
                          `</div>`
                        );
                    }
                  }

                  return "";
                },
              }),
            })
          );
        });

        this.$refs.optOuts.getDataAsync();
      }
    },

    async update(update) {
      try {
        await this.adminUpdate(update);
        this.$refs.optOuts.getDataAsync();
        this.dialogs.edit = false;
        this.optOut = null;
      } catch (error) {
        console.error(error); // ok we are good to goooo
        this.error = error;
      }
    },
  },
  watch: {
    statusFilter: {
      handler() {
        this.$nextTick(() => {
          this.$refs.optOuts.getDataAsync();
        });
      },
    },
    coverageFilter: {
      handler() {
        this.$nextTick(() => {
          this.$refs.optOuts.getDataAsync();
        });
      },
    },
    "selectedPlan.Id": {
      immediate: true,
      handler() {
        this.load();
      },
    },
  },
};
</script>

<template>
  <div>
    <material-card
      title="OptOuts"
      :color="$vuetify.theme.themes.light.secondary"
      icon="work"
    >
      <v-row class="ml-3" style="max-width: 600px">
        <v-col>
          <v-select
            label="Status"
            v-model="statusFilter"
            :items="statusFilters"
            item-value="value"
            item-text="text"
          ></v-select>
        </v-col>
        <v-col>
          <v-select
            label="Coverage"
            v-model="coverageFilter"
            :items="coverageGroupItems"
            item-value="id"
            item-text="name"
          ></v-select>
        </v-col>
      </v-row>
      <pbas-data-table
        :rowActions="rowActions"
        ref="optOuts"
        :dataProvider="provider"
        :exporters="$exporters"
      >
      </pbas-data-table>
    </material-card>
    <v-dialog
      scrollable
      v-model="dialogs.edit"
      max-width="700"
      @input="dialogs.edit = false"
    >
      <material-card
        v-if="selected"
        title="Update OptOut"
        :color="$vuetify.theme.themes.light.primary"
        icon="edit"
        style="overflow: auto"
      >
        <edit-optOut-form
          v-for="optOut in selected.OptOuts"
          :key="optOut.Id"
          :emailText="configuration.emailTemplate"
          @save="update"
          @close="dialogs.edit = false"
          ref="editOptOutForm"
          :optOut="optOut"
        />
      </material-card>
    </v-dialog>
  </div>
</template>
